import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";

import {
  ScloneCasting, ScloneEntry, SCLONE_CASTING_OFF, SCLONE_CASTING_ON, formatBytes, scloneCastingLabel,
} from '@/features/sclones/sclonesSlice';
import { useSclones, useSclonePermissions, UploadProgress } from '@/features/sclones/useSclones';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import { NumberText } from '@/components/Common/Field';
import MenuItem from '@/components/Common/MenuItem';
import { ButtonsModal, DeleteModal } from '@/components/Common/Modals';

// CloneSettings — Settings › "Clone Settings". A GLOBAL catalog (no project
// scope) of encrypted Samsung Tizen clone bundles, keyed by model + firmware
// version + casting (ON / OFF / Both). All data + permission logic lives in
// the useSclones / permission hooks; this file is the presentation layer,
// split into focused pieces:
//   - CloneSettings   — page shell + delete-confirm wiring
//   - UploadControl   — hidden file input, casting choice, upload button,
//                       progress ring
//   - OptionsCell     — per-row download / delete menu
// The store owns the API (getSclones / uploadSclone / deleteSclone).

// A grid row is a ScloneEntry plus derived display fields and the per-row
// action wiring the cell renderer reads.
interface ScloneRow extends ScloneEntry {
  castingLabel:  string;
  sizeLabel:     string;
  updatedLabel:  string;
  canDelete:     boolean;
  requestDelete: React.MutableRefObject<(e: ScloneEntry) => void>;
}

const CloneSettings = () => {
  const { t } = useTranslation();
  const perms = useSclonePermissions();
  const { entries, legacyBackend, uploading, progress, checkFile, upload, remove } = useSclones();

  const [deleteTarget, setDeleteTarget] = React.useState<ScloneEntry | null>(null);

  // ON / OFF / Both / the bare number — see scloneCastingLabel.
  const castingText = React.useCallback((casting: ScloneCasting | null | undefined) => {
    const l = scloneCastingLabel(casting);
    return t(l.key, { value: l.value });
  }, [t]);

  // Cell renderer reaches back into the page via a ref (mirrors the other
  // catalog grids), so a row's delete button calls the live handler.
  const requestDelete = React.useRef<(e: ScloneEntry) => void>(() => { return; });
  requestDelete.current = (e: ScloneEntry) => setDeleteTarget(e);

  const cols = React.useMemo(() => [
    { field: "model",        headerName: t("clone_settings:grid:model"),   flex: 1, minWidth: 160 },
    { field: "version",      headerName: t("clone_settings:grid:version"), flex: 1, minWidth: 220 },
    { field: "castingLabel", headerName: t("clone_settings:grid:casting"), flex: 0, minWidth: 170, maxWidth: 200 },
    { field: "sizeLabel",    headerName: t("clone_settings:grid:size"),    flex: 0, minWidth: 110, maxWidth: 140 },
    { field: "updatedLabel", headerName: t("clone_settings:grid:updated"), flex: 0, minWidth: 180, maxWidth: 220 },
    ...((perms.canChange || perms.canDelete)
      ? [{
          field: "options", headerName: t("clone_settings:grid:options"),
          flex: 0, filter: false, minWidth: 90, maxWidth: 90, cellRenderer: OptionsCell,
        }]
      : []),
  ], [t, perms.canChange, perms.canDelete]);

  const rows = React.useMemo<ScloneRow[]>(() => entries.map((e) => ({
    ...e,
    castingLabel:  castingText(e.casting),
    sizeLabel:     formatBytes(e.size),
    updatedLabel:  e.updated_at ? new Date(e.updated_at * 1000).toLocaleString() : "",
    canDelete:     perms.canDelete,
    requestDelete,
  })), [entries, perms.canDelete, castingText]);

  const processDelete = React.useCallback(async (_obj: Object, answer: string) => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (answer !== "yes" || !target) return;
    await remove(target);
  }, [deleteTarget, remove]);

  if (!perms.canView) {
    return (
      <FlexRow direction="column"
               align="start"
               className="bg-white px-[32px] pt-[32px]">
        <div className="text-[32px]/[48px]">{ t('clone_settings:caption') }</div>
        <div className="text-[14px] text-[--Dark-Grey] pt-[16px]">
          { t('clone_settings:no_access') }
        </div>
      </FlexRow>
    );
  }

  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('clone_settings:caption') }
      </div>
      <div>
        <NumberText>{ rows.length }</NumberText>
        { ' ' + t('clone_settings:total') }
      </div>
      <Divider className="my-[8px]" />
      { perms.canChange &&
        <UploadControl uploading={uploading}
                       progress={progress}
                       legacyBackend={legacyBackend}
                       checkFile={checkFile}
                       onUpload={upload} />
      }
    </FlexRow>

    <FlexRow direction="column"
             align="start"
             className={["py-[16px]", "px-[32px]"]}>
      <Grid rowData={rows}
            columnDefs={cols}
            filter={false}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            pageSize={10} />
    </FlexRow>

    {deleteTarget &&
      <DeleteModal title={t('clone_settings:delete:caption')}
                   message={t('clone_settings:delete:message', {
                     model:   deleteTarget.model,
                     version: deleteTarget.version,
                     casting: castingText(deleteTarget.casting),
                   })}
                   onResult={processDelete}>
        <></>
      </DeleteModal>
    }
  </React.Fragment>
  );
};

// ---------------------------------------------------------------------------
// UploadControl — the "Upload Bundle" button plus a live progress ring. Owns
// its own hidden file input. After a zip is picked it ASKS which Casting the
// clone was exported with (ON / OFF / Cancel — deliberately no default, so a
// clone can't land on the wrong setting) and only then hands file + casting
// up to the page's upload(). An older backend has no casting: no question,
// a warning instead.
// ---------------------------------------------------------------------------
interface UploadControlProps {
  uploading:     boolean;
  progress:      UploadProgress | null;
  legacyBackend: boolean;
  checkFile:     (file: File) => boolean;
  onUpload:      (file: File, casting: ScloneCasting | null) => void;
}

const UploadControl: React.FC<UploadControlProps> = ({ uploading, progress, legacyBackend, checkFile, onUpload }) => {
  const { t } = useTranslation();
  const fileRef = React.useRef<HTMLInputElement>(null);

  // The picked zip waiting for its casting choice (dialog open while set).
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);

  const openPicker = () => {
    if (!uploading && !pendingFile) fileRef.current?.click();
  };

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files && evt.target.files[0];
    // Reset the input so re-selecting the SAME file fires change again.
    evt.target.value = "";
    if (!file || !checkFile(file)) return;
    if (legacyBackend) onUpload(file, null);
    else               setPendingFile(file);
  };

  // "on" / "off" upload; "cancel" or "" (Esc / backdrop close) drops the file.
  const onCastingChoice = (_obj: object, answer: string) => {
    const file = pendingFile;
    setPendingFile(null);
    if (!file) return;
    if (answer === "on")  onUpload(file, SCLONE_CASTING_ON);
    if (answer === "off") onUpload(file, SCLONE_CASTING_OFF);
  };

  return (
  <React.Fragment>
    {/* accept is a hint only — the backend validates the zip contents. */}
    <input ref={fileRef}
           type="file"
           accept=".zip,application/zip"
           className="hidden"
           onChange={onChange} />
    <FlexRow flex={0} className="gap-[16px] mt-[20px] items-center">
      <Button caption={uploading
                        ? t('clone_settings:upload:uploading')
                        : t('clone_settings:upload:caption')}
              icon="upload"
              isDisabled={uploading || !!pendingFile}
              className="uppercase"
              onClick={openPicker} />
      { uploading && progress &&
        <UploadProgressRing progress={progress} />
      }
      { legacyBackend &&
        <span className="text-[13px] text-[--Warning]">
          { t('clone_settings:upload:legacy_backend') }
        </span>
      }
    </FlexRow>

    {pendingFile &&
      <ButtonsModal title={t('clone_settings:upload:casting_title')}
                    message={t('clone_settings:upload:casting_question')}
                    buttons={[
                      { value: "cancel", caption: t('modals:warning:cancel'),       layout: "white" },
                      { value: "off",    caption: t('clone_settings:casting:off'), layout: "default" },
                      { value: "on",     caption: t('clone_settings:casting:on'),  layout: "default" },
                    ]}
                    onResult={onCastingChoice}>
        <div className="r12 text-[--Dark-Grey] pb-[16px] break-all">{ pendingFile.name }</div>
      </ButtonsModal>
    }
  </React.Fragment>
  );
};

// ---------------------------------------------------------------------------
// UploadProgressRing — green ring (percentage in the centre) with the live
// byte counter beside it.
// ---------------------------------------------------------------------------
const UploadProgressRing: React.FC<{ progress: UploadProgress }> = ({ progress }) => {
  const { t } = useTranslation();
  const pct = progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;

  return (
  <FlexRow flex={0} className="gap-[10px] items-center">
    <CircularProgress aria-label={t('clone_settings:upload:uploading')}
                      size="md"
                      color="success"
                      showValueLabel={true}
                      value={pct} />
    <span className="text-[13px] text-[--Dark-Grey] whitespace-nowrap">
      { formatBytes(progress.loaded) + ' / ' + formatBytes(progress.total) }
    </span>
  </FlexRow>
  );
};

// ---------------------------------------------------------------------------
// OptionsCell — per-row action menu (download / delete). Download uses
// entry.url VERBATIM (it is opaque — never reconstruct it from model/version).
// ---------------------------------------------------------------------------
const OptionsCell = (props: { data: ScloneRow }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const data = props.data;

  const handleDownload = () => {
    setOpen(false);
    if (data.url) window.open(data.url, "_blank");
  };

  const handleDelete = () => {
    setOpen(false);
    data.requestDelete.current(data);
  };

  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{ content: "rounded-none" }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        <MenuItem icon="download"
                  label={t('clone_settings:download')}
                  onClick={handleDownload} />
        { data.canDelete &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('clone_settings:delete:caption')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export default CloneSettings;
